import React from 'react';
import { createPortal } from 'react-dom';
import { AgnesButton } from '@agnes/ui';
import ServiceDctf from '@/services/ServiceDctf';
import UtilsMask from '@/utils/UtilsMask';
import { notify } from '@/stores/useToast';
import { moedaBR } from '@/utils/locale';

type Any = any;

interface Props {
  dctf: Any;
}

interface ChargeOption {
  codigoEncargo: string | null;
  descricaoEncargo: string | null;
}

interface DetailItem {
  id: string;
  codReceita?: string | null;
  ctDescricaoTributo?: string | null;
  debitoApurado?: string | number | null;
  pagamentos?: string | number | null;
  valorExclusao?: string | number | null;
  retencaoLei?: string | number | null;
  valorParcelamento?: string | number | null;
  outrosCreditos?: string | number | null;
  saldoPagar?: string | number | null;
  hasDcomp?: boolean;
  compensacoes?: { id: string; numDoc?: string | null; vlCompensado?: string | number | null }[];
}

const formatNumDoc = (value?: string | null) => {
  if (!value) return '-';
  const numbers = value.replace(/\D/g, '');
  if (numbers.length !== 24) return value;
  return numbers.replace(/(\d{5})(\d{5})(\d{6})(\d{1})(\d{2})(\d{4})/, '$1.$2.$3.$4.$5-$6');
};

export default function DctfDetails({ dctf }: Props) {
  const [items, setItems] = React.useState<DetailItem[]>([]);
  const [charges, setCharges] = React.useState<ChargeOption[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(10);
  const [sortBy, setSortBy] = React.useState('id');
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');
  const [draftChargeCode, setDraftChargeCode] = React.useState('');
  const [chargeCode, setChargeCode] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [selectedDcomp, setSelectedDcomp] = React.useState<DetailItem | null>(null);

  React.useEffect(() => {
    let active = true;
    setCharges([]);
    void ServiceDctf.buscarEncargos(dctf.id)
      .then(({ data }) => {
        if (active) setCharges(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) notify.error('Erro ao carregar os códigos de encargo da DCTF.');
      });
    return () => {
      active = false;
    };
  }, [dctf.id]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setItems([]);
    setTotal(0);
    void ServiceDctf.buscarDetalhes(dctf.id, {
      page,
      size,
      chargeCode,
      sort: `${sortBy},${sortOrder}`,
    })
      .then(({ data }) => {
        if (!active) return;
        setItems(Array.isArray(data?.content) ? data.content : []);
        setTotal(Number(data?.totalElements ?? 0));
      })
      .catch(() => {
        if (active) notify.error('Erro ao carregar os detalhes da declaração DCTF.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [dctf.id, page, size, chargeCode, sortBy, sortOrder]);

  const totalPages = Math.max(Math.ceil(total / size), 1);
  const toggleSort = (key: string) => {
    if (sortBy === key) setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(key);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const applyFilter = () => {
    setPage(0);
    setChargeCode(draftChargeCode);
  };

  const clearFilter = () => {
    setDraftChargeCode('');
    setChargeCode('');
    setPage(0);
  };

  const sortLabel = (key: string, label: string) => (
    <button
      type="button"
      aria-label={`Ordenar por ${label}`}
      aria-sort={sortBy === key ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => toggleSort(key)}
      className="font-semibold hover:text-agnes-primary"
    >
      {label}
      {sortBy === key ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''}
    </button>
  );

  return (
    <div className="flex flex-col gap-4">
      <section className="overflow-x-auto rounded-lg border border-agnes-border">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-agnes-bg-subtle text-agnes-text">
            <tr>
              <th className="px-3 py-2">Empregador</th>
              <th className="px-3 py-2">Período de apuração</th>
              <th className="px-3 py-2">Débito apurado</th>
              <th className="px-3 py-2">Deduções</th>
              <th className="px-3 py-2">Outros créditos</th>
              <th className="px-3 py-2">Pagamentos</th>
              <th className="px-3 py-2">Saldo a pagar</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-agnes-border">
              <td className="px-3 py-3">
                <div>{dctf.empresaRazaoSocial || dctf.razaoSocial || '-'}</div>
                <div className="text-xs text-agnes-text-muted">
                  {UtilsMask.cnpj(dctf.empresaCnpj || dctf.cnpj)}
                </div>
              </td>
              <td className="px-3 py-3">{dctf.periodoApur || '-'}</td>
              <td className="px-3 py-3">{moedaBR(dctf.debitoApurado ?? dctf.debitoTotal)}</td>
              <td className="px-3 py-3">{moedaBR(dctf.deducoes)}</td>
              <td className="px-3 py-3">{moedaBR(dctf.outrosCreditos)}</td>
              <td className="px-3 py-3">{moedaBR(dctf.pagamentos)}</td>
              <td className="px-3 py-3">{moedaBR(dctf.saldoAPagar ?? dctf.saldoAPagarTotal)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="rounded-lg border border-agnes-border bg-agnes-bg-surface p-4">
        <div className="flex flex-col items-end gap-3 md:flex-row">
          <div className="w-full flex-1">
            <label
              htmlFor="dctf-charge-code"
              className="mb-1 block text-sm font-medium text-agnes-text"
            >
              Pesquisar por código de encargo
            </label>
            <select
              id="dctf-charge-code"
              value={draftChargeCode}
              onChange={(event) => setDraftChargeCode(event.target.value)}
              className="h-10 w-full rounded-md border border-agnes-border bg-agnes-bg-surface px-3 text-sm text-agnes-text"
            >
              <option value="">Todos os encargos</option>
              {charges.map((charge) => (
                <option
                  key={charge.codigoEncargo ?? charge.descricaoEncargo}
                  value={charge.codigoEncargo ?? ''}
                >
                  {[charge.codigoEncargo, charge.descricaoEncargo].filter(Boolean).join(' - ')}
                </option>
              ))}
            </select>
          </div>
          <div className="flex shrink-0 gap-2">
            <AgnesButton
              variant="primary"
              disabled={draftChargeCode === chargeCode || loading}
              onClick={applyFilter}
            >
              Aplicar
            </AgnesButton>
            <AgnesButton
              variant="secondary"
              disabled={!draftChargeCode && !chargeCode}
              onClick={clearFilter}
            >
              Limpar
            </AgnesButton>
          </div>
        </div>
      </section>

      <div className="overflow-x-auto rounded-lg border border-agnes-border">
        <table className="w-full min-w-[1150px] text-right text-sm">
          <thead className="bg-[var(--tp-bg-table-header)] text-agnes-text">
            <tr className="h-12">
              <th colSpan={4} />
              <th
                colSpan={4}
                className="border-x border-[#cfc9e5] bg-[#efedf7] text-center font-bold text-[#442d96]"
              >
                Outros Créditos
              </th>
              <th colSpan={2} />
            </tr>
            <tr>
              <th className="px-3 py-3 text-left">{sortLabel('codReceita', 'Código')}</th>
              <th className="px-3 py-3 text-left">
                {sortLabel('ctDescricaoTributo', 'Descrição')}
              </th>
              <th className="px-3 py-3">{sortLabel('debitoApurado', 'Débito')}</th>
              <th className="px-3 py-3">{sortLabel('pagamentos', 'Pago')}</th>
              <th className="border-l border-[#cfc9e5] px-3 py-3">Exclusão</th>
              <th className="px-3 py-3">Suspensão</th>
              <th className="px-3 py-3">Parcelamento</th>
              <th className="border-r border-[#cfc9e5] px-3 py-3">Compensação</th>
              <th className="px-3 py-3">{sortLabel('saldoPagar', 'Saldo')}</th>
              <th className="px-3 py-3 text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-agnes-text-muted">
                  Carregando encargos…
                </td>
              </tr>
            ) : items.length > 0 ? (
              items.map((item) => (
                <tr key={item.id} className="border-t border-agnes-border text-agnes-text">
                  <td className="px-3 py-3 text-left">{item.codReceita || '-'}</td>
                  <td className="px-3 py-3 text-left">{item.ctDescricaoTributo || '-'}</td>
                  <td className="px-3 py-3">{moedaBR(item.debitoApurado)}</td>
                  <td className="px-3 py-3">{moedaBR(item.pagamentos)}</td>
                  <td className="border-l border-[#cfc9e5] px-3 py-3">
                    {moedaBR(item.valorExclusao)}
                  </td>
                  <td className="px-3 py-3">{moedaBR(item.retencaoLei)}</td>
                  <td className="px-3 py-3">{moedaBR(item.valorParcelamento)}</td>
                  <td className="border-r border-[#cfc9e5] px-3 py-3">
                    {moedaBR(item.outrosCreditos)}
                  </td>
                  <td className="px-3 py-3">{moedaBR(item.saldoPagar)}</td>
                  <td className="px-3 py-3 text-center">
                    <button
                      type="button"
                      aria-label={`Ver DCOMP de ${item.codReceita ?? 'encargo'}`}
                      disabled={!item.hasDcomp}
                      onClick={() => setSelectedDcomp(item)}
                      className="rounded px-2 py-1 text-agnes-primary disabled:cursor-not-allowed disabled:text-agnes-text-muted"
                    >
                      Ver DCOMP
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-agnes-text-muted">
                  Nenhum encargo encontrado para esta seleção.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <nav
        aria-label="Paginação de encargos"
        className="flex flex-wrap items-center justify-end gap-3 text-sm"
      >
        <label className="flex items-center gap-2">
          Registros por página
          <select
            aria-label="Registros por página"
            value={size}
            onChange={(event) => {
              setSize(Number(event.target.value));
              setPage(0);
            }}
            className="h-9 rounded-md border border-agnes-border bg-agnes-bg-surface px-2"
          >
            {[10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </label>
        <span>
          {total} registros · Página {page + 1} de {totalPages}
        </span>
        <AgnesButton
          variant="secondary"
          disabled={page === 0 || loading}
          onClick={() => setPage((value) => value - 1)}
        >
          Anterior
        </AgnesButton>
        <AgnesButton
          variant="secondary"
          disabled={page + 1 >= totalPages || loading}
          onClick={() => setPage((value) => value + 1)}
        >
          Próxima
        </AgnesButton>
      </nav>

      {selectedDcomp &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="dctf-dcomp-title"
              className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-xl bg-agnes-bg-surface p-5 shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between gap-4">
                <h2 id="dctf-dcomp-title" className="text-lg font-semibold text-agnes-text">
                  Compensações DCOMP
                </h2>
                <button
                  type="button"
                  aria-label="Fechar DCOMP"
                  onClick={() => setSelectedDcomp(null)}
                >
                  Fechar
                </button>
              </div>
              {selectedDcomp.compensacoes?.length ? (
                <ul className="flex flex-col gap-3">
                  {selectedDcomp.compensacoes.map((compensacao) => (
                    <li key={compensacao.id} className="rounded-md border border-agnes-border p-3">
                      <span className="font-medium">{formatNumDoc(compensacao.numDoc)}</span>
                      <span className="ml-2">{moedaBR(compensacao.vlCompensado)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-agnes-text-muted">Nenhuma compensação encontrada.</p>
              )}
            </section>
          </div>,
          document.body,
        )}
    </div>
  );
}
